<script setup lang="ts">
import { Ref, ref, PropType, watch } from 'vue';
import {
  CurrentCaseServiceInterface,
  getContainer,
  NamedBindings,
  CoreConfigurationInterface,
  CaseStatusServiceInterface,
} from '@dandelion/core';

const props = defineProps({
  mapForStatus: {
    type: Map as PropType<Map<string, Map<string, number>>>,
    required: true,
    default: new Map(),
  },
  statusExpanded: {
    type: Boolean as PropType<boolean>,
    required: true,
    default: false,
  },
});

const currentCaseService: CurrentCaseServiceInterface = getContainer().get(
  NamedBindings.CurrentCaseService
) as CurrentCaseServiceInterface;

const currentStatusService: CaseStatusServiceInterface = getContainer().get(
  NamedBindings.CaseStatusService
) as CaseStatusServiceInterface;

const config = getContainer().get(
  NamedBindings.CoreConfiguration
) as CoreConfigurationInterface;
let mainCrfName: string = config.listOfCaseTypes[0].getMainCRF().crfName;
let mainCrfStatus: Ref<number> = ref(0);
let childrenCrfs: Ref<Map<string, number>> = ref(new Map());
const isCaseOpened = currentCaseService.isCaseOpened;

const emit = defineEmits<{ (e: 'toggleStatusDisplayer'): void }>();

watch(props.mapForStatus, () => {
  mainCrfStatus.value = props.mapForStatus.get('*')!.get(mainCrfName)!;
  const mapArray = Array.from(props.mapForStatus.get('*')!) as Array<any>;
  mapArray.shift();
  childrenCrfs.value = new Map(mapArray);
});

function toggleStatusDisplayer() {
  emit('toggleStatusDisplayer');
}

function refreshStatus() {
  currentStatusService.updateStatusFn();
}
</script>

<template>
  <div
    v-if="isCaseOpened"
    id="status-displayer"
    class="shadow-md"
    :class="{ expanded: statusExpanded }"
  >
    <table>
      <thead>
        <tr>
          <th v-html="mainCrfName"></th>
          <th
            v-for="[crfName] in childrenCrfs"
            v-if="statusExpanded"
            :key="crfName"
            v-html="crfName"
          ></th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="status-cell">
            <span
              :class="'status-' + mainCrfStatus"
              v-html="mainCrfStatus"
            ></span>
          </td>
          <td
            v-for="[crfName, crfStatusValue] in childrenCrfs"
            v-if="statusExpanded"
            class="status-cell"
            :key="crfName"
          >
            <span
              :class="'status-' + crfStatusValue"
              v-html="crfStatusValue"
            ></span>
          </td>
        </tr>
      </tbody>
    </table>
    <ion-icon
      id="refresh-icon"
      @click="refreshStatus()"
      :name="'refresh-outline'"
    ></ion-icon>
    <ion-icon
      id="toggle-expand-icon"
      :class="{ expanded: statusExpanded }"
      @click="toggleStatusDisplayer()"
      :name="statusExpanded ? 'caret-forward-outline' : 'caret-back-outline'"
    ></ion-icon>
  </div>
</template>

<style>
#utils-buttons-section {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 4.5vw;
  top: 5rem;
  right: 0.75vw;
  z-index: 999;
  padding: 0;
}

#status-displayer {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 4.5vw;
  top: 0;
  right: 0vw;
  background-color: #fff;
  border-radius: 1rem;
  transition: 0.3s ease;
  z-index: 999;
  padding: 0;
  text-transform: capitalize;
}

#status-displayer.expanded {
  width: 76vw;
  height: 6rem;
  right: 41vw;
}

#status-displayer.full-expanded {
  width: 93vw;
  right: 49.5vw;
}

#status-displayer table {
  height: 100%;
}

#status-displayer thead th:first-of-type {
  border-top-left-radius: 1rem;
}

#status-displayer thead th:last-of-type {
  border-top-right-radius: 1rem;
}

#status-displayer tbody td {
  border-radius: 0.25rem;
}

#status-displayer.expanded tbody td:first-of-type {
  border-bottom-left-radius: 1rem;
}

#status-displayer.expanded tbody td:last-of-type {
  border-bottom-right-radius: 1rem;
}

#status-displayer ion-icon {
  background-color: #388087;
  color: #fff;
  border-radius: 50%;
  padding: 0.5rem;
  top: 1.5rem;
  min-width: 2rem;
  min-height: 2rem;
  margin-bottom: 0.25rem;
  cursor: pointer;
  visibility: inherit;
}

#status-displayer #toggle-expand-icon.expanded {
  position: absolute;
  right: -4vw;
  top: 1.5rem;
  margin: 0;
}

.status-close {
  position: fixed;
  transform: translateX(100%);
  opacity: 0;
}

#status-displayer .status-cell span {
  display: inline-block;
  width: 1.5rem;
  aspect-ratio: 1;
  border-radius: 50%;
  /*text-indent: -9999px;*/
  overflow: hidden;
  white-space: nowrap;
  box-shadow: inset 0 0 5px #989ba1;
}

#status-displayer .status-cell span.status-0 {
  background-color: white;
}

#status-displayer .status-cell span.status-1 {
  background-color: red;
}

#status-displayer .status-cell span.status-2 {
  background-color: yellow;
}

#status-displayer .status-cell span.status-3,
#status-displayer .status-cell span.status-4 {
  background-color: green;
}
</style>
